import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  IconButton,
  useMediaQuery,
  ClickAwayListener,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCompanyFinancialScores } from "../hooks/useCompanyFinancialScores";

const ZONE_CONFIG = {
  weak: {
    label: "Financial Concerns",
    badge: "Weak",
    description: "Multiple financial red flags identified across profitability, leverage, and efficiency criteria.",
    color: "#ef4444",
    bgColor: "rgba(239, 68, 68, 0.08)",
    borderColor: "rgba(239, 68, 68, 0.2)",
    rangeLabel: "0 – 3",
  },
  moderate: {
    label: "Moderate Health",
    badge: "Moderate",
    description: "Average financial position. Some positive indicators balanced with areas of concern.",
    color: "#f59e0b",
    bgColor: "rgba(245, 158, 11, 0.08)",
    borderColor: "rgba(245, 158, 11, 0.2)",
    rangeLabel: "4 – 6",
  },
  strong: {
    label: "Financially Strong",
    badge: "Strong",
    description: "Strong financial fundamentals across profitability, leverage, and operational efficiency.",
    color: "#22c55e",
    bgColor: "rgba(34, 197, 94, 0.08)",
    borderColor: "rgba(34, 197, 94, 0.2)",
    rangeLabel: "7 – 9",
  },
} as const;

type Zone = keyof typeof ZONE_CONFIG;

const TOTAL_CRITERIA = 9;

const getZone = (score: number): Zone =>
  score >= 7 ? "strong" : score >= 4 ? "moderate" : "weak";

interface PiotroskiScoreChartProps {
  ticker: string;
  height?: number;
  renderCard?: boolean;
}

const PiotroskiScoreChart: React.FC<PiotroskiScoreChartProps> = ({
  ticker,
  renderCard = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const { data, isLoading, error } = useCompanyFinancialScores(ticker);

  const wrapContent = (content: React.ReactNode) => {
    if (!renderCard) return <>{content}</>;
    return (
      <Card
        elevation={0}
        sx={{
          backgroundColor: isDark ? "#212121" : "#f5f5f5",
          height: "100%",
        }}
      >
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>{content}</CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return wrapContent(
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
          <Skeleton variant="text" width={160} height={20} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1 }}>
          <Skeleton variant="text" width={80} height={52} />
          <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: "2px" }} />
        </Box>
        <Skeleton variant="text" width="80%" height={18} sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
          {Array.from({ length: TOTAL_CRITERIA }).map((_, i) => (
            <Skeleton key={i} variant="circular" width={16} height={16} />
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={52} sx={{ borderRadius: "2px" }} />
          ))}
        </Box>
      </Box>
    );
  }

  if (!data || data.length === 0 || !!error) {
    return wrapContent(
      <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: isDark ? "#9ca3af" : "#64748b", mb: 1 }}>
          No Piotroski Score Data
        </Typography>
        <Typography variant="body2" sx={{ color: isDark ? "#6b7280" : "#94a3b8", maxWidth: 340 }}>
          Not available for this company. Common for smaller companies, recent IPOs, or international stocks.
        </Typography>
      </Box>
    );
  }

  const score = data[0].piotroskiScore;
  const zone = getZone(score);
  const config = ZONE_CONFIG[zone];

  return wrapContent(
    <>
      {/* Card header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.8125rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: isDark ? "#e5e7eb" : "#1d1d1f",
          }}
        >
          Piotroski F-Score
        </Typography>
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <div>
            <Tooltip
              title="The Piotroski F-Score evaluates 9 binary financial criteria across profitability, leverage, and operating efficiency. Scores of 7–9 indicate financial strength, 4–6 moderate health, and 0–3 suggest financial concerns."
              arrow
              open={isMobile ? tooltipOpen : undefined}
              disableFocusListener={isMobile}
              disableHoverListener={isMobile}
              disableTouchListener={isMobile}
              onClose={() => setTooltipOpen(false)}
            >
              <IconButton
                size="small"
                onClick={() => isMobile && setTooltipOpen(!tooltipOpen)}
                sx={{ color: isDark ? "#6b7280" : "#9ca3af", p: 0.5 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </ClickAwayListener>
      </Box>

      {/* Score hero + zone badge */}
      <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
          <Typography sx={{ fontSize: "2.75rem", fontWeight: 700, lineHeight: 1, color: config.color }}>
            {score}
          </Typography>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 400, color: isDark ? "#6b7280" : "#9ca3af", lineHeight: 1 }}>
            /9
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: config.bgColor,
            border: `1px solid ${config.borderColor}`,
            borderRadius: "2px",
            mb: 0.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: config.color, fontSize: "0.75rem" }}>
            {config.badge}
          </Typography>
        </Box>
      </Box>

      {/* Context description */}
      <Typography sx={{ color: isDark ? "#9ca3af" : "#6b7280", fontSize: "0.8125rem", lineHeight: 1.5 }}>
        {config.description}
      </Typography>

      {/* Criteria fill dots */}
      <Box sx={{ mt: 2.5, mb: 2.5 }}>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {Array.from({ length: TOTAL_CRITERIA }).map((_, i) => {
            const filled = i < score;
            return (
              <Box
                key={i}
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: filled ? config.color : "transparent",
                  border: `2px solid ${filled ? config.color : (isDark ? "#374151" : "#d1d5db")}`,
                  flexShrink: 0,
                }}
              />
            );
          })}
        </Box>
        <Typography
          sx={{ fontSize: "0.75rem", color: isDark ? "#6b7280" : "#9ca3af", mt: 1 }}
        >
          {score} of {TOTAL_CRITERIA} criteria met
        </Typography>
      </Box>

      {/* Zone reference grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
        {(["weak", "moderate", "strong"] as Zone[]).map((z) => {
          const isActive = zone === z;
          const c = ZONE_CONFIG[z];
          return (
            <Box
              key={z}
              sx={{
                p: 1.5,
                backgroundColor: isActive ? c.bgColor : "transparent",
                border: `1px solid ${isActive ? c.borderColor : theme.palette.divider}`,
                borderRadius: "2px",
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.6875rem",
                  color: isActive ? c.color : (isDark ? "#6b7280" : "#9ca3af"),
                  display: "block",
                  mb: 0.25,
                }}
              >
                {c.badge}
              </Typography>
              <Typography sx={{ fontSize: "0.625rem", color: isDark ? "#4b5563" : "#cbd5e1" }}>
                {c.rangeLabel}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
};

export default PiotroskiScoreChart;
